import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FilterSelect, HpTag, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt } from '../../lib/format';
import { invalidateMapping, useProjectTargets, type ProjectMapRow } from './queries';

const NONE = '__none__';

/** One CRM "Poptaný projekt" option per row with a Back Office project to assign it to. */
export function ProjectMapTable({ rows, canManage }: { rows: ProjectMapRow[]; canManage: boolean }) {
  const { t } = useTranslation(['performance', 'common']);
  const targets = useProjectTargets();
  const decide = useMutation(trpc.performance.mapping.decideProject.mutationOptions({ onSuccess: invalidateMapping }));

  return (
    <>
      <ErrorAlert error={decide.error ?? targets.error} className="bo-alert--inset" />
      <TableScroll label={t('mapping.projects.title')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('mapping.columns.crmProject')}</th>
            <th className="bo-num">{t('mapping.columns.cases')}</th>
            <th>{t('mapping.columns.project')}</th>
            <th>{t('mapping.columns.status')}</th>
          </BrutTheadRow>
          <tbody>
            {rows.map((r) => {
              const tone = r.status === 'confirmed' ? 'success' : r.status === 'suggested' ? 'warning' : 'neutral';
              return (
                <tr key={r.crmOptionId}>
                  <td>
                    <span className="bo-strong">{r.label}</span>{' '}
                    <small className="bo-mono bo-muted">{r.crmOptionId}</small>
                  </td>
                  <td className="bo-num">{fmtInt(r.cases)}</td>
                  <td>
                    {canManage ? (
                      <FilterSelect
                        value={r.status === 'rejected' ? NONE : (r.projectId ?? '')}
                        disabled={decide.isPending}
                        aria-label={t('mapping.columns.project')}
                        onChange={(e) =>
                          decide.mutate({
                            crmOptionId: r.crmOptionId,
                            projectId: e.target.value === NONE || e.target.value === '' ? null : e.target.value,
                          })
                        }
                      >
                        <option value="">
                          {r.status === null ? t('mapping.pickProject') : t('mapping.pickProject')}
                        </option>
                        {(targets.data ?? []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                        <option value={NONE}>{t('mapping.noProject')}</option>
                      </FilterSelect>
                    ) : r.status === 'rejected' ? (
                      <span className="bo-muted">{t('mapping.noProject')}</span>
                    ) : (
                      <span>{r.projectName ?? '—'}</span>
                    )}
                    {r.status === 'suggested' && r.score !== null && (
                      <small className="bo-muted"> {t('mapping.score', { pct: Math.round(r.score * 100) })}</small>
                    )}
                  </td>
                  <td>
                    <HpTag tone={tone}>{t(`mapping.status.${r.status ?? 'unmapped'}`)}</HpTag>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="bo-cell-empty">
                  {t('mapping.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
    </>
  );
}
